/* global mainScript */
import React, { useState, useRef, useEffect } from "react";
import styled from "@emotion/styled";
import PropTypes from "prop-types";

//style rules
import { Colors, Typography, Sizes } from "../../styles";

const DropDown = ({ filters, selectedSchool, setSelectedSchool }) => {
	const [openDropDown, setOpenDropDown] = useState(false);
	const [updatedFilters, setUpdatedFilters] = useState(filters);
	const [searchTerm, setSearchTerm] = useState("");

	const inputRef = useRef(null);
	const dropDownNode = useRef();

	const handleOutsideClick = (e) => {
		if (dropDownNode.current && !dropDownNode.current.contains(e.target)) {
			setOpenDropDown(!openDropDown);
		}
	};

	useEffect(() => {
		if (openDropDown) {
			document.addEventListener("mousedown", handleOutsideClick);
			inputRef.current.focus();
		}
		return () => {
			document.removeEventListener("mousedown", handleOutsideClick);
		};
	}, [openDropDown]);

	useEffect(() => {
		updateFilters();
	}, [searchTerm]);

	const toggleDropDown = () => {
		setOpenDropDown((currentValue) => !currentValue);
	};

	const handleFilterSelect = (slug) => {
		setSelectedSchool(slug);
		toggleDropDown();
	};

	const handleChange = (event) => {
		setSearchTerm(event.target.value);
	};

	const renderFilterSearch = () => {
		return (
			<FilterSearchContainer>
				<FilterSearch
					type="text"
					value={searchTerm}
					onChange={handleChange}
					placeholder="Search for Your School "
					ref={inputRef}
				/>
			</FilterSearchContainer>
		);
	};

	const updateFilters = () => {
		if (searchTerm.length === 0) {
			return setUpdatedFilters(filters);
		}
		const formattedSearchTerm = searchTerm.split(" ").join("-"); // Replace spaces with dashes
		const filterResults = filters.filter((schoolBoard) =>
			schoolBoard.slug.includes(formattedSearchTerm.toLowerCase())
		);
		setUpdatedFilters(filterResults);
	};

	const renderFilters = () => {
		if (updatedFilters.length === 0) {
			return (
				<DropDownListItem>
					<Item>No results available</Item>
				</DropDownListItem>
			);
		}

		return (
			updatedFilters &&
			updatedFilters.length > 0 &&
			updatedFilters.map((filter) => {
				return (
					<DropDownListItem
						key={filter.slug}
						onClick={() => handleFilterSelect(filter.slug)}
					>
						<Item dangerouslySetInnerHTML={{ __html: filter.name }} />
					</DropDownListItem>
				);
			})
		);
	};

	return (
		<DropDownContainer ref={dropDownNode}>
			<Title>Select Your School</Title>
			<DropDownTrigger onClick={toggleDropDown} onKeyPress={toggleDropDown}>
				{selectedSchool ? selectedSchool : "Search For Your School"}
			</DropDownTrigger>
			{openDropDown && (
				<DropDownListContainer>
					{renderFilterSearch()}
					<DropDownList>{renderFilters()}</DropDownList>
				</DropDownListContainer>
			)}
		</DropDownContainer>
	);
};

export default DropDown;

DropDown.propTypes = {
	filters: PropTypes.array,
	selectedSchool: PropTypes.string,
	setSelectedSchool: PropTypes.func,
};

const Title = styled.p`
	color: ${Colors.base.black};
	font-weight: ${Typography.fontWeight.black};
	letter-spacing: -0.0015em;
	margin-bottom: 1.5rem;
	text-align: left !important;
`;

// fake centering using the top value
const DropDownContainer = styled.div`
	width: 100%;
	position: relative;
`;

const DropDownTrigger = styled.button`
	padding: 1.3rem 1.5rem;
	color: ${Colors.base.black};
	border: 1px solid ${Colors.base.black};
	border-radius: 2px;
	font-weight: 700;
	font-size: 1.3rem;
	line-height: 1.6rem;
	cursor: pointer;
	display: flex;
	justify-content: space-between;
	gap: 1.1rem;
	height: fit-content;
	min-height: 4rem;
	background: none;
	transition: all 0.5s;
	width: 91%;
	opacity: 70%;
	text-align: left;

	&::after {
		content: url(${mainScript.assetPath +
		"images/svg/icons/icon-down-arrow.svg"});
	}
`;

const DropDownListContainer = styled.div`
	position: absolute;
	top: 42px;
	border: 1px solid ${Colors.base.black};
	border-radius: 2px;
	z-index: 2;
	width: 91%;
`;

const DropDownList = styled.ul`
	display: flex;
	flex-direction: column;
	gap: 1.6rem;
	margin: 0;
	padding: 1.6rem 1.5rem 2rem;
	max-height: 140px;
	background-color: ${Colors.base.white};
	overflow-y: scroll;

	@media only screen and (min-width: ${Sizes.screens.large}) {
		max-height: 214px;
	}

	/* Styling the scrollbar */
	&::-webkit-scrollbar {
		background-color: #979393;
		width: 7px;
		border-radius: 12px; /* Curved radius */
	}

	&::-webkit-scrollbar-thumb {
		width: 7px;
		background-color: #d9d9d9;
		border-radius: 12px;
	}

	/* Styling the scrollbar in Firefox */
	scrollbar {
		width: 7px;
		background-color: #979393;
		border-radius: 12px;
	}

	scrollbar thumb {
		width: 7px;
		background-color: #d9d9d9;
		border-radius: 12px;
	}
`;

const FilterSearchContainer = styled.div`
	display: flex;
	justify-content: space-between;
	gap: 1.1rem;
`;

const FilterSearch = styled.input`
	border: none !important;
	color: ${Colors.base.black};
	margin-bottom: 0 !important;
	padding: 1.3rem 1.5rem 0 !important;
	height: 4rem;

	&:active {
		border: none !important;
	}

	&:focus {
		border: none !important;
		outline: none !important;
	}

	&::-webkit-input-placeholder {
		color: rgba(31, 31, 31, 0.6);
		font-size: 1.3rem;
		font-weight: 700;
	}
`;

const DropDownListItem = styled.li`
	margin: 0;
	padding: 0;
	list-style: none;
	display: flex;
	flex-direction: row;
	justify-content: space-between;
`;

const Item = styled.label`
	margin-bottom: 0;
	color: ${Colors.base.black};
	font-size: 1.3rem !important;
    line-height: 1.6rem;
	font-weight: 700;
`;
